import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Layout } from "../../components/layout/Layout";
import { api, formatApiError } from "../../lib/api";
import { useAuth, hasPerm } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { ArrowLeft, FileText, CheckCircle2, Ban, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Doit rester synchronisé avec ISSUE_LABELS côté backend (routers/depot_routes.py).
const ISSUE_LABEL_MAP = {
  manquant: "Produit manquant",
  qte_manquante: "Quantité manquante",
  doublon: "Doublon",
  introuvable: "Introuvable",
};

function totals(commande) {
  const total = (commande.lines || []).reduce((s, l) => s + (l.quantite_attendue || 0), 0);
  const recue = (commande.lines || []).reduce((s, l) => s + Math.min(l.quantite_recue || 0, l.quantite_attendue || 0), 0);
  const percent = total > 0 ? Math.round((recue / total) * 100) : 0;
  return { total, recue, percent };
}

// Pastille cliquable, sur le même principe que celle du dépôt : chaque clic
// valide la réception d'une unité de l'article ; un clic sur un article déjà
// complet le remet à 0 (pour corriger une erreur de manipulation).
function LinePill({ line, onTap, disabled }) {
  const recue = line.quantite_recue || 0;
  const attendue = line.quantite_attendue || 0;
  const done = recue >= attendue && attendue > 0;
  return (
    <button
      onClick={onTap}
      disabled={disabled}
      title={disabled ? "Quantité bloquée : problème signalé par le dépôt" : undefined}
      data-testid={`commande-line-pill-${line.id}`}
      className={`min-h-[48px] min-w-[64px] px-4 rounded-full font-heading text-lg font-bold border-2 transition-colors duration-150 active:scale-95 disabled:opacity-60 disabled:active:scale-100 disabled:cursor-not-allowed ${
        done ? "bg-emerald-500 border-emerald-500 text-white" : "bg-card border-foreground/20 text-foreground hover:border-primary"
      }`}
    >
      {recue}/{attendue}
    </button>
  );
}

export default function CommandeDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [commande, setCommande] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingMissing, setPendingMissing] = useState([]);
  const [description, setDescription] = useState("");
  // Empêche les doubles clics pendant qu'une action réseau est en cours (valider,
  // annuler, confirmer) : sans ce verrou, un clic répété pendant que le serveur
  // met du temps à répondre pouvait déclencher deux requêtes en parallèle — la
  // première ajoutait bien les articles au stock, la seconde échouait ensuite
  // (commande déjà traitée) et affichait une erreur trompeuse, alors que la
  // validation avait en réalité réussi.
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/commandes/${id}`);
    setCommande(data);
  };
  useEffect(() => { load(); }, [id]);

  if (!(hasPerm(user, "commande", "view") || hasPerm(user, "depot"))) return <Navigate to="/" replace />;
  if (!commande) return null;

  const canResolve = commande.status === "envoyee" && (user.is_admin || (hasPerm(user, "commande", "accept_refuse") && user.shop_id === commande.shop_id));
  const { total, recue, percent } = totals(commande);

  // Quantité manquante par ligne, pour surligner en rouge les articles
  // manquants dans la liste principale. Tant que la commande est "envoyée",
  // ce sont les problèmes signalés par le dépôt (produit manquant, quantité
  // manquante, doublon, introuvable) ; une fois résolue en "non conforme",
  // c'est le détail final retenu lors de la validation par la boutique.
  const missingByLine = {};
  if (commande.status === "non_conforme") {
    for (const i of commande.non_conforme_items || []) {
      missingByLine[i.line_id] = { qty: i.quantite_manquante, raison: i.raison };
    }
  } else if (commande.status === "envoyee") {
    for (const l of commande.lines || []) {
      if (l.issue_type) missingByLine[l.id] = { qty: l.issue_quantite_manquante, raison: ISSUE_LABEL_MAP[l.issue_type] };
    }
  }
  const depotIssueLines = (commande.lines || []).filter((l) => l.issue_type);

  const tapLine = async (lineId) => {
    try {
      const { data } = await api.post(`/commandes/${id}/lines/${lineId}/tap`);
      setCommande(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const annulerVerification = async () => {
    if (busy) return;
    if (!window.confirm("Annuler la vérification de cette commande ? Aucun article ne sera ajouté au stock, et le dépôt sera informé de l'annulation.")) return;
    setBusy(true);
    try {
      await api.post(`/commandes/${id}/annuler`);
      toast.success("Vérification annulée : le dépôt a été prévenu");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  // Valide la réception : si tous les articles que la boutique contrôle elle-même
  // ont été entièrement cliqués, la commande passe conforme directement. Sinon,
  // ceux restés incomplets sont automatiquement considérés comme manquants et une
  // confirmation est demandée avant d'envoyer le signalement au dépôt.
  //
  // Les lignes déjà signalées par le dépôt (issue_type) sont volontairement
  // exclues de ce calcul : le dépôt les a déjà comptabilisées comme manquantes
  // avant l'envoi (et leur quantité reçue est bloquée, voir LinePill), donc
  // elles ne doivent pas, à elles seules, faire passer la commande en "non
  // conforme" — seuls les articles que la boutique constate elle-même manquants
  // à la réception physique doivent déclencher une non-conformité.
  const validerReception = async () => {
    if (busy) return;
    const missing = (commande.lines || [])
      .filter((l) => !l.issue_type)
      .map((l) => ({
        line_id: l.id,
        description: l.description,
        quantite_manquante: Math.max(0, (l.quantite_attendue || 0) - (l.quantite_recue || 0)),
        raison: "",
      }))
      .filter((i) => i.quantite_manquante > 0);

    if (missing.length === 0) {
      setBusy(true);
      try {
        await api.post(`/commandes/${id}/resolve-conforme`);
        toast.success("Commande validée : articles ajoutés au stock");
        await load();
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      } finally {
        setBusy(false);
      }
      return;
    }

    setPendingMissing(missing);
    setDescription("");
    setConfirmOpen(true);
  };

  const confirmNonConforme = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/commandes/${id}/resolve-non-conforme`, { items: pendingMissing, description });
      toast.success("Réception validée : les articles cliqués ont été ajoutés au stock, un message a été envoyé au dépôt pour les articles manquants");
      setConfirmOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout title="Commande">
      <div className="flex items-center gap-3 sm:gap-4 mb-6 no-print">
        <Link to="/commandes" data-testid="commande-back-button" className="p-2 rounded-lg border border-border hover:bg-accent transition-colors duration-200 shrink-0">
          <ArrowLeft size={18} />
        </Link>
        <div className="min-w-0">
          <p className="font-heading text-xl sm:text-2xl font-bold truncate" data-testid="commande-detail-title">CMD {commande.numero}</p>
          {commande.destinataire && (
            <p className="text-xs text-muted-foreground truncate" data-testid="commande-detail-destinataire">{commande.destinataire}</p>
          )}
          <p className="text-sm text-muted-foreground">Boutique : {commande.shop_nom}</p>
        </div>
      </div>

      <a href={`${process.env.REACT_APP_BACKEND_URL}${commande.delivery_pdf_url}`} target="_blank" rel="noreferrer" className="inline-flex mb-6 no-print">
        <Button variant="outline" className="gap-2" data-testid="commande-view-delivery"><FileText size={16} />Voir le bon de livraison</Button>
      </a>

      {canResolve && (
        <>
          <p className="text-sm text-muted-foreground mb-2 no-print" data-testid="commande-progress">Reçu {recue}/{total} · {percent}%</p>
          <div className="h-2 rounded-full bg-secondary mb-6 overflow-hidden no-print">
            <div className="h-full bg-primary transition-all duration-300" style={{ width: `${percent}%` }} />
          </div>
        </>
      )}

      {commande.status === "envoyee" && depotIssueLines.length > 0 && (
        <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-4 mb-6 no-print" data-testid="commande-depot-issue-banner">
          <p className="font-semibold text-red-600 flex items-center gap-2"><AlertTriangle size={16} /> Produits manquants signalés par le dépôt</p>
          <ul className="text-sm text-red-600 mt-2 space-y-0.5 list-disc list-inside" data-testid="commande-depot-issue-list">
            {depotIssueLines.map((l) => (
              <li key={l.id}>
                {l.description} — {ISSUE_LABEL_MAP[l.issue_type]}
                {l.issue_type === "qte_manquante" && ` (x${l.issue_quantite_manquante})`}
              </li>
            ))}
          </ul>
          <p className="text-xs text-red-600/80 mt-2">
            Ces articles (ou quantités) ont déjà été comptabilisés comme manquants par le dépôt : ils sont automatiquement
            ignorés et n'empêchent pas la commande d'être validée conforme. Seuls les articles que vous laissez non cochés
            ci-dessous feront passer la commande en non conforme.
          </p>
        </div>
      )}

      <div className="space-y-2 mb-6" data-testid="commande-line-list">
        {(commande.lines || []).map((line) => {
          const missing = missingByLine[line.id];
          const isMissing = missing !== undefined;
          return (
            <div
              key={line.id}
              className={`rounded-lg border p-3 flex items-center justify-between gap-3 ${
                isMissing ? "border-red-500/40 bg-red-500/10" : "border-border bg-card"
              }`}
              data-testid={`commande-line-${line.id}`}
            >
              <div className="min-w-0">
                <p className={`font-medium break-words ${isMissing ? "text-red-600" : ""}`}>{line.description}</p>
                <p className={`text-xs ${isMissing ? "text-red-600" : "text-muted-foreground"}`}>
                  Quantité : {line.quantite_attendue}
                  {isMissing && ` — manquant : ${missing.qty}${missing.raison ? ` (${missing.raison})` : ""}`}
                </p>
              </div>
              {canResolve && <LinePill line={line} onTap={() => tapLine(line.id)} disabled={isMissing} />}
            </div>
          );
        })}
      </div>

      {commande.status !== "envoyee" && (
        <div className="rounded-lg border border-border bg-card p-4 mb-6" data-testid="commande-resolution-summary">
          <p className="font-semibold">
            {commande.status === "conforme" && "✅ Commande conforme"}
            {commande.status === "non_conforme" && "⚠️ Commande non conforme"}
            {commande.status === "annulee" && "🚫 Vérification annulée"}
          </p>
          {commande.status === "non_conforme" && (commande.non_conforme_items || []).length > 0 && (
            <ul className="text-sm text-muted-foreground mt-2 space-y-0.5 list-disc list-inside" data-testid="commande-nc-summary-list">
              {commande.non_conforme_items.map((i, idx) => (
                <li key={idx}>{i.description} — manquant : {i.quantite_manquante}{i.raison ? ` (${i.raison})` : ""}</li>
              ))}
            </ul>
          )}
          {(commande.status === "conforme" || commande.status === "non_conforme") && depotIssueLines.length > 0 && (
            <div className="mt-2" data-testid="commande-depot-issue-recap">
              <p className="text-xs text-muted-foreground">Articles déjà signalés manquants par le dépôt avant l'envoi (n'ont pas déclenché de non-conformité) :</p>
              <ul className="text-sm text-muted-foreground mt-1 space-y-0.5 list-disc list-inside">
                {depotIssueLines.map((l) => (
                  <li key={l.id}>{l.description} — {ISSUE_LABEL_MAP[l.issue_type]}{l.issue_type === "qte_manquante" && ` (x${l.issue_quantite_manquante})`}</li>
                ))}
              </ul>
            </div>
          )}
          {commande.status === "annulee" && (
            <p className="text-sm text-muted-foreground mt-1">Aucun article n'a été ajouté au stock. Le dépôt a été informé de l'annulation.</p>
          )}
          {commande.resolution_note && <p className="text-sm text-muted-foreground mt-1">{commande.resolution_note}</p>}
          <p className="text-xs text-muted-foreground mt-1">Traité par {commande.resolved_by_nom}</p>
        </div>
      )}

      {canResolve && (
        <div className="flex flex-wrap gap-3 no-print">
          <Button data-testid="commande-valider-button" onClick={validerReception} disabled={busy} className="gap-2 bg-emerald-600 hover:bg-emerald-700">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Valider la réception
          </Button>
          <Button data-testid="commande-annuler-button" variant="outline" onClick={annulerVerification} disabled={busy} className="gap-2">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Ban size={16} />} Annuler la vérification
          </Button>
        </div>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="commande-non-conforme-dialog" className="max-w-xl">
          <DialogHeader><DialogTitle>Articles manquants détectés</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Ces articles n'ont pas été entièrement cliqués comme reçus. Ils ne seront pas ajoutés au stock, et un
              message sera envoyé au dépôt avec le détail. Les autres articles seront ajoutés au stock normalement.
            </p>
            <ul className="text-sm space-y-1 max-h-64 overflow-y-auto pr-3 -mr-3" data-testid="commande-nc-summary-preview">
              {pendingMissing.map((i) => (
                <li key={i.line_id} className="rounded-lg border border-red-500/30 bg-red-500/10 p-2.5 text-red-600">
                  {i.description} — manquant : {i.quantite_manquante}{i.raison ? ` (${i.raison})` : ""}
                </li>
              ))}
            </ul>
            <Textarea data-testid="commande-nc-description" placeholder="Description du problème (optionnel)" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <DialogFooter>
            <Button data-testid="commande-nc-submit" onClick={confirmNonConforme} disabled={busy} className="gap-2">
              {busy && <Loader2 size={16} className="animate-spin" />} Envoyer le signalement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
